import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useMutation } from "@tanstack/react-query";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { appSettingsHelper } from "libs/helpers/src/lib/appSettings";
import { AnimatePresence, motion } from "motion/react";
import { memo, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { Button, EyeIcon } from "../../../atoms";
import { XIcon } from "../../../atoms/icons/XIcon";
import { AppSettingsNavigationPreview } from "../AppSettingsNavigation/AppSettingsNavigationPreview";
import { AppSettingsContext } from "../components/AppSettingsContext";
import { AppSettingsOption } from "../components/AppSettingsOption";

const SortableRow = memo(function SortableRow({
  id,
  children,
}: {
  id: string;
  children: React.ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative" as const,
    zIndex: isDragging ? 60 : undefined,
    willChange: transform ? "transform" : undefined,
  } as React.CSSProperties;
  return (
    <div ref={setNodeRef} style={style} className="">
      <AppSettingsOption.Container>
        <div {...attributes} {...listeners}>
          <AppSettingsOption.Handle />
        </div>
        {children}
      </AppSettingsOption.Container>
    </div>
  );
});

export const AppSettingsHomePage = () => {
  const { t } = useTranslation();
  const { homePageSettings, setHomePageSettings } =
    useContext(AppSettingsContext);
  const ctaLabels = {
    buyMembership: t("page.settings.appSettings.homePage.buyMembership"),
    buyCreditPack: t("page.settings.appSettings.homePage.buyCreditPack"),
  } satisfies Record<(typeof homePageSettings.ctas)[number]["id"], string>;
  const widgetLabels = {
    gymCapacity: t("page.settings.appSettings.homePage.gymCapacity"),
    appointmentBooking: t(
      "page.settings.appSettings.homePage.appointmentBooking",
    ),
    classBooking: t("page.settings.appSettings.homePage.classBooking"),
    classAttendance: t("page.settings.appSettings.homePage.classAttendance"),
    appointmentAttendance: t(
      "page.settings.appSettings.homePage.appointmentAttendance",
    ),
    gymAttendance: t("page.settings.appSettings.homePage.gymAttendance"),
    newsFeed: t("page.settings.appSettings.homePage.newsFeed"),
  } satisfies Record<(typeof homePageSettings.widgets)[number]["id"], string>;
  const { mutateAsync: updateAppHomePage } = useMutation(
    apiV2.staff.appSettings.mutations.updateHomePageSettings(),
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const [showPreview, setShowPreview] = useState(false);

  const widgetItems = useMemo(
    () => homePageSettings.widgets.filter((w) => !w.dontShowOnUI),
    [homePageSettings.widgets],
  );
  const handleDrag = <T extends "ctas" | "widgets">(
    type: T,
    activeId?: UniqueIdentifier,
    overId?: UniqueIdentifier,
  ) => {
    if (activeId === overId) return;
    const list = homePageSettings[type];
    const ids = list.map((item) => String(item.id));
    const oldIndex = ids.indexOf(String(activeId));
    const newIndex = ids.indexOf(String(overId));
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const reordered = arrayMove(list as unknown as [], oldIndex, newIndex);
    setHomePageSettings({
      ...homePageSettings,
      [type]: reordered,
    });
  };

  return (
    <>
      {createPortal(
        <AnimatePresence>
          {showPreview && (
            <motion.div
              className="absolute inset-0 z-50 flex flex-col items-center justify-start bg-gray-100 pt-4 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="z-10 flex w-full flex-row items-center justify-between gap-4 p-4 pb-0">
                <div className="text-xl font-semibold text-gray-950">
                  {t("pages.settings.appSettings.homePage.preview")}
                </div>
                <Button
                  intent="transparent"
                  onClick={() => {
                    setShowPreview(false);
                  }}
                >
                  <XIcon className="stroke-gray-950" />
                </Button>
              </div>
              <AppSettingsNavigationPreview className="pt-0" />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
      <div className="flex size-full flex-col justify-between">
        <div className="flex flex-col gap-1">
          <div className="text-lg font-semibold text-gray-950">
            {t("page.settings.appSettings.homePage.title")}
          </div>
          <div className="text-sm font-medium text-gray-500">
            {t("page.settings.appSettings.homePage.description")}
          </div>
        </div>
        <div className="relative flex size-full">
          <div className="absolute inset-0 -mx-6 flex flex-col gap-4 overflow-y-auto overflow-x-visible p-6">
            <AppSettingsOption.Container>
              <AppSettingsOption.Switch
                value={homePageSettings.checkInWidgetEnabled.enabled}
                onChange={() => {
                  setHomePageSettings({
                    ...homePageSettings,
                    checkInWidgetEnabled: {
                      ...homePageSettings.checkInWidgetEnabled,
                      enabled: !homePageSettings.checkInWidgetEnabled.enabled,
                    },
                  });
                }}
              />
              <AppSettingsOption.Label>
                {t("page.settings.appSettings.homePage.checkIn")}
              </AppSettingsOption.Label>
            </AppSettingsOption.Container>
            <AppSettingsOption.Separator />

            <div className="flex flex-col gap-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragOver={({ active, over }) => {
                  handleDrag("ctas", active?.id, over?.id);
                }}
                onDragEnd={({ active, over }) => {
                  handleDrag("ctas", active?.id, over?.id);
                }}
              >
                <SortableContext
                  items={homePageSettings.ctas.map((c) => c.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {homePageSettings.ctas.map((widget) => (
                    <SortableRow key={widget.id} id={widget.id}>
                      <AppSettingsOption.Switch
                        value={widget.enabled}
                        onChange={() =>
                          setHomePageSettings({
                            ...homePageSettings,
                            ctas: homePageSettings.ctas.map((c) =>
                              c.id === widget.id
                                ? { ...c, enabled: !c.enabled }
                                : c,
                            ),
                          })
                        }
                        onPointerDownCapture={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <AppSettingsOption.Label>
                        {ctaLabels[widget.id]}
                      </AppSettingsOption.Label>
                    </SortableRow>
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            <AppSettingsOption.Separator />
            <div className="flex flex-col gap-2">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragOver={({ active, over }) => {
                  handleDrag("widgets", active?.id, over?.id);
                }}
                onDragEnd={({ active, over }) => {
                  handleDrag("widgets", active?.id, over?.id);
                }}
              >
                <SortableContext
                  items={widgetItems.map((w) => w.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {widgetItems.map((widget) => (
                    <SortableRow key={widget.id} id={widget.id}>
                      <AppSettingsOption.Switch
                        value={widget.enabled}
                        onChange={() =>
                          setHomePageSettings({
                            ...homePageSettings,
                            widgets: homePageSettings.widgets.map((w) =>
                              w.id === widget.id
                                ? { ...w, enabled: !w.enabled }
                                : w,
                            ),
                          })
                        }
                        onPointerDownCapture={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <AppSettingsOption.Label>
                        {widgetLabels[widget.id]}
                      </AppSettingsOption.Label>
                    </SortableRow>
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </div>
        </div>
        <div className="-mx-6 -mb-6 flex flex-row justify-end gap-2 border-t border-gray-300 px-6 py-4">
          <Button
            className="w-1/2 gap-1 lg:hidden"
            onClick={async () => {
              setShowPreview(true);
            }}
          >
            <EyeIcon className="stroke-gray-950" />
            {t("pages.settings.appSettings.homePage.preview")}
          </Button>
          <Button
            intent="secondary"
            className="max-lg:w-1/2 lg:px-12"
            onClick={async () => {
              if (homePageSettings) {
                await updateAppHomePage(
                  appSettingsHelper.mapFEtoBE(homePageSettings),
                );
              }
            }}
          >
            {t("portal.common.save")}
          </Button>
        </div>
      </div>
    </>
  );
};
